import { SessionFilter } from '@molecules/SessionFilter';
import {
  logout,
  SessionRole,
  sortSessionsByUserRole,
  useBulkGetProfiles,
  useGetBulkSessionData,
  useGetBulkSessionVoteCounts,
  useGetCanonicalCategoriesByEvent,
  useGetCanonicalTagsByEvent,
  useGetEvent,
  useGetSessionsByEventId,
  useGetSessionsByTagsAndCategories,
  useGetUserViewedSessions,
} from '@sunrise-ui/api-client';
import {
  SessionCard,
  SessionDetailsModal,
  SpeakerFilter,
  withAuthGate,
} from '@sunrise-ui/composites';

// This implementation uses a two-step data fetching approach:
// 1. Fetch sessions by event ID
// 2. Use the session IDs to fetch bulk data (tags, categories, attendees) in a single request,
//    reducing the number of API calls and improving performance.
// The SessionFilter component fetches its own tags and categories data

import { ArrowLeft01Icon, FilterIcon } from '@hugeicons/core-free-icons';
import type { Category, SessionOutput, Tag } from '@sunrise-ui/api/events/api';
import { SessionStatus } from '@sunrise-ui/api/events/api';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  BaseCardsList,
  Button,
  ButtonVariants,
  Colors,
  ExternalLink,
  Header,
  IconButton,
  isNative,
  LogoIcon,
  PageContainer,
  TextReg,
  Toggle,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  useSafeLocalSearchParams,
  useSetHeader,
} from '@sunrise-ui/primitives';
import { Redirect, router } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import React, { useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

function SessionVoting() {
  const { isMobile } = useMediaQueries();
  const { eventId, fromEvents } = useSafeLocalSearchParams<{
    eventId: string;
    fromEvents?: string;
  }>();

  // Determine responsive layout flags - condensed view at 767px and below
  const isCondensedView = isMobile; // <= 767px only

  const styles = useDynamicStyles(createStyles, { isMobile });
  const { isAuthenticated, currentUserId, openAuthFlowModal } = useAuthStore();

  // Fetch event data to display event name
  const { data: event } = useGetEvent(eventId);
  const [filterCategoryIds, setFilterCategoryIds] = useState<Set<string>>(
    new Set(),
  );
  const [filterTagIds, setFilterTagIds] = useState<Set<string>>(new Set());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showViewedOnly, setShowViewedOnly] = useState(false);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<
    string | undefined
  >();

  // Fetch previously viewed sessions for the current user and event
  // Wait for credentials to be fully loaded to prevent race condition with bearer token
  const { data: viewedSessions, isSuccess: isViewedSessionsSuccess } =
    useGetUserViewedSessions(eventId, currentUserId);

  // Compute viewedSessionIds from API results only (no local state needed)
  const viewedSessionIds = useMemo(() => {
    const viewedIds = new Set<string>();

    // Add previously viewed sessions from the API
    if (
      isViewedSessionsSuccess &&
      viewedSessions &&
      viewedSessions.length > 0
    ) {
      viewedSessions.forEach((session: { id: string | undefined }) => {
        if (session.id) viewedIds.add(session.id as string);
      });
    }

    return viewedIds;
  }, [isViewedSessionsSuccess, viewedSessions]);

  // Pre-cache canonical tag and category data to prevent redundant API calls
  // when opening SessionDetailsModal (which uses useGetSessionTagsAndCategories)
  const { data: tags, isLoading: isLoadingTags } =
    useGetCanonicalTagsByEvent(eventId);
  const { data: categories, isLoading: isLoadingCategories } =
    useGetCanonicalCategoriesByEvent(eventId);

  // Get all available filter options (both categories and tags)
  const allFilterOptions = [
    ...(categories
      ?.map((cat: Category) => cat.id)
      .filter((id): id is string => Boolean(id)) ?? []),
    ...(tags
      ?.map((tag: Tag) => tag.id)
      .filter((id): id is string => Boolean(id)) ?? []),
  ];

  const isLoadingFilterData = isLoadingTags || isLoadingCategories;

  const hasFilter = filterCategoryIds.size > 0 || filterTagIds.size > 0;

  const { data: filteredSessions } = useGetSessionsByTagsAndCategories(
    {
      category_ids: [...filterCategoryIds],
      tag_ids: [...filterTagIds],
      match_all_categories: false,
      match_all_tags: false,
    },
    eventId,
  );
  // Use combined endpoint to fetch all session voting page data at once
  // Fetch sessions by event ID with shuffled flag for voting page
  const { data: allSessions, isLoading: isLoadingSessions } =
    useGetSessionsByEventId(eventId, true);

  // Filter for only voting sessions for the voting page
  const sessions = useMemo(() => {
    if (!allSessions) return undefined;
    return allSessions.filter(
      (session: SessionOutput) => session.status === SessionStatus.Voting,
    );
  }, [allSessions]);

  // Extract session IDs for bulk data fetch
  const sessionIds =
    sessions?.map((session: SessionOutput) => session.id) || [];

  // Fetch bulk data for all sessions
  const {
    data: bulkSessionData,
    isLoading: isLoadingBulkData,
    isSuccess: isBulkDataSuccess,
  } = useGetBulkSessionData({
    sessionIds,
  });

  // Extract user_ids from bulk session data
  const user_ids =
    bulkSessionData?.session_data
      ?.flatMap(
        (sessionData) =>
          sessionData.data.attendees?.map((attendee) => attendee.user_id) || [],
      )
      .filter(Boolean) || [];

  // Fetch bulk user data for all users
  const { isLoading: isLoadingProfiles } = useBulkGetProfiles(user_ids);

  // Fetch bulk vote counts for all sessions
  const { isLoading: isLoadingVotes } = useGetBulkSessionVoteCounts({
    sessionIds,
    eventId, // Add eventId for consistent cache key
    enabled: sessionIds.length > 0,
  });

  // Create filter controls (filter button, speaker search, toggle)
  const createFilterControls = () => {
    // Check if any filters are applied and count them
    let activeFilterCount = filterCategoryIds.size + filterTagIds.size;

    // Add 1 for viewed toggle if it's set to "viewed" (true)
    if (showViewedOnly) {
      activeFilterCount += 1;
    }

    // Add 1 for speaker filter if a speaker is selected
    if (selectedSpeakerId) {
      activeFilterCount += 1;
    }

    const hasActiveFilters = activeFilterCount > 0;

    // Conditional back button when navigated from events screen
    const backButton =
      fromEvents === 'true' ? (
        <IconButton
          icon={ArrowLeft01Icon}
          size={isMobile ? 'small' : 'medium'}
          backgroundColor="transparent"
          onPress={() => {
            router.navigate('/events');
          }}
        />
      ) : null;

    const filterButton = (
      <View style={styles.filterButtonContainer}>
        <IconButton
          icon={FilterIcon}
          size={isMobile ? 'small' : 'medium'}
          loading={isLoadingFilterData}
          disabled={isLoadingFilterData || !allFilterOptions.length}
          backgroundColor={hasActiveFilters ? Colors.opacity20 : 'transparent'}
          onPress={() => {
            setIsFilterOpen(true);
          }}
        />
        {hasActiveFilters && (
          <View style={styles.filterCountBadge}>
            <TextReg style={styles.filterCountText}>
              {activeFilterCount}
            </TextReg>
          </View>
        )}
      </View>
    );

    const speakerDropdown = (
      <SpeakerFilter
        eventId={eventId}
        selectedSpeakerId={selectedSpeakerId}
        onSpeakerSelect={setSelectedSpeakerId}
      />
    );

    // Create toggle component for viewed/unviewed filter
    const toggleComponent = isAuthenticated ? (
      <Toggle
        value={showViewedOnly}
        onValueChange={setShowViewedOnly}
        leftLabel="All"
        rightLabel="Viewed"
      />
    ) : null;

    return { backButton, filterButton, speakerDropdown, toggleComponent };
  };

  // Create desktop header controls layout
  const createDesktopControls = () => {
    const { backButton, filterButton, speakerDropdown } =
      createFilterControls();

    return (
      <View style={styles.headerLeftContainer}>
        {backButton}
        {filterButton}
        {speakerDropdown}
      </View>
    );
  };

  const loginButton = isAuthenticated ? (
    <TouchableOpacity onPress={logout}>
      <TextReg style={styles.loginText}>Log Out</TextReg>
    </TouchableOpacity>
  ) : (
    <TouchableOpacity
      style={styles.loginLink}
      onPress={() => {
        openAuthFlowModal({ isDismissible: true }, { currentStep: 'login' });
      }}
    >
      <TextReg style={styles.loginText}>Log In</TextReg>
    </TouchableOpacity>
  );

  // Get filter controls for different view types
  const { toggleComponent: headerToggle } = createFilterControls();

  // Create tickets button component
  const handleTicketsPress = async () => {
    await WebBrowser.openBrowserAsync(
      `https://checkout.joincommuniful.com/events/${eventId}`,
      {
        windowFeatures: {
          popup: false,
        },
      },
    );
  };

  const ticketsButton = (
    <Button
      variant={ButtonVariants.PURPLE}
      size={isMobile ? 'small' : 'medium'}
      onPress={handleTicketsPress}
      labelStyle={styles.ticketsButton}
    >
      Tickets
    </Button>
  );

  // Set up header with responsive layout and height
  useSetHeader(
    !isCondensedView
      ? {
          header: () => (
            <Header
              headerLeft={
                <View style={styles.headerLeftContainer}>
                  {createDesktopControls()}
                </View>
              }
              headerRight={
                <View style={styles.headerRightContainer}>
                  {headerToggle}
                  {loginButton}
                  {ticketsButton}
                </View>
              }
              desktopLogo={LogoIcon}
              mobileLogo={LogoIcon}
              showShadow={true}
            />
          ),
        }
      : {
          header: () => (
            <Header
              headerLeft={
                <View style={styles.headerLeftContainer}>
                  {createFilterControls().backButton}
                  {createFilterControls().filterButton}
                </View>
              }
              headerRight={
                <View style={styles.headerRightContainer}>
                  {loginButton}
                  {ticketsButton}
                </View>
              }
              desktopLogo={LogoIcon}
              mobileLogo={LogoIcon}
              showShadow={true} // Enable yellow bottom shadow
            />
          ),
        },
  );

  // Sort sessions to prioritize user's sessions where they are speaker/moderator
  const baseSortedSessions = useMemo(() => {
    if (!isAuthenticated || !isBulkDataSuccess) {
      return sessions;
    }
    return sortSessionsByUserRole(sessions, currentUserId, bulkSessionData);
  }, [
    sessions,
    isAuthenticated,
    currentUserId,
    bulkSessionData,
    isBulkDataSuccess,
  ]);

  // Apply speaker filter and unviewed filter to sorted sessions
  const filteredBySpokenSessions = useMemo(() => {
    if (!selectedSpeakerId || !bulkSessionData?.session_data) {
      return baseSortedSessions;
    }

    return baseSortedSessions?.filter((session: SessionOutput) => {
      const sessionData = bulkSessionData.session_data?.find(
        (data) => data.session_id === session.id,
      );

      if (!sessionData?.data.attendees) return false;

      return sessionData.data.attendees.some(
        (attendee) =>
          attendee.user_id === selectedSpeakerId &&
          (attendee.role === SessionRole.SPEAKER ||
            attendee.role === SessionRole.MODERATOR),
      );
    });
  }, [baseSortedSessions, selectedSpeakerId, bulkSessionData]);

  // Apply viewed filter to speaker-filtered sessions
  const sortedSessions = useMemo(() => {
    if (!showViewedOnly || !isAuthenticated) {
      return filteredBySpokenSessions;
    }
    return filteredBySpokenSessions?.filter((session: SessionOutput) =>
      viewedSessionIds.has(session.id as string),
    );
  }, [
    filteredBySpokenSessions,
    showViewedOnly,
    isAuthenticated,
    viewedSessionIds,
  ]);

  // Sort filtered sessions the same way
  const baseSortedFilteredSessions = useMemo(() => {
    if (!isAuthenticated || !isBulkDataSuccess) {
      return filteredSessions?.sessions;
    }
    return sortSessionsByUserRole(
      filteredSessions?.sessions,
      currentUserId,
      bulkSessionData,
    );
  }, [
    filteredSessions?.sessions,
    isAuthenticated,
    currentUserId,
    bulkSessionData,
    isBulkDataSuccess,
  ]);

  // Apply speaker filter to tag/category filtered sessions
  const filteredBySpokenFilteredSessions = useMemo(() => {
    if (!selectedSpeakerId || !bulkSessionData?.session_data) {
      return baseSortedFilteredSessions;
    }

    return baseSortedFilteredSessions?.filter((session: SessionOutput) => {
      const sessionData = bulkSessionData.session_data?.find(
        (data) => data.session_id === session.id,
      );

      if (!sessionData?.data.attendees) return false;

      return sessionData.data.attendees.some(
        (attendee) =>
          attendee.user_id === selectedSpeakerId &&
          (attendee.role === SessionRole.SPEAKER ||
            attendee.role === SessionRole.MODERATOR),
      );
    });
  }, [baseSortedFilteredSessions, selectedSpeakerId, bulkSessionData]);

  // Apply viewed filter to speaker-filtered tag/category sessions
  const sortedFilteredSessions = useMemo(() => {
    if (!showViewedOnly || !isAuthenticated) {
      return filteredBySpokenFilteredSessions;
    }
    return filteredBySpokenFilteredSessions?.filter((session: SessionOutput) =>
      viewedSessionIds.has(session.id as string),
    );
  }, [
    filteredBySpokenFilteredSessions,
    showViewedOnly,
    isAuthenticated,
    viewedSessionIds,
  ]);

  const numColumns = 1;
  // Define a comprehensive loading state that includes all data fetching operations
  const isLoading =
    isLoadingSessions ||
    (sessionIds.length > 0 &&
      (isLoadingBulkData || isLoadingVotes || isLoadingProfiles)) ||
    !isBulkDataSuccess;

  // Redirect to events page if no sessions are available
  if (!isLoading && !sessions?.length) {
    return <Redirect href="/" />;
  }

  return (
    <PageContainer>
      {event?.name && (
        <View style={styles.eventNameContainer}>
          <TextReg style={styles.eventNameText}>{event.name}</TextReg>
          {event.description && (
            <View style={styles.eventDescriptionContainer}>
              <TextReg style={styles.eventDescriptionText}>
                {event.description}{' '}
                <ExternalLink
                  href="https://www.tcstartupweek.com"
                  style={styles.learnMoreLinkContainer}
                >
                  <TextReg style={styles.learnMoreLink}>Learn more</TextReg>
                </ExternalLink>
              </TextReg>
            </View>
          )}
        </View>
      )}

      <BaseCardsList
        isLoading={isLoading}
        onRefresh={async () => {}}
        data={hasFilter ? sortedFilteredSessions : sortedSessions}
        numColumns={numColumns}
        key={numColumns}
        contentContainerStyle={styles.cardsList}
        renderItem={({ item: session }: { item: SessionOutput }) => {
          return (
            <SessionCard
              key={session.id}
              sessionId={session.id}
              eventId={eventId}
              style={styles.sessionCard}
            />
          );
        }}
        keyExtractor={(item) => item.id as string}
      />
      {/* Spunky footer section - only show when no filters are applied */}
      {!isLoading &&
        filterCategoryIds.size === 0 &&
        filterTagIds.size === 0 && (
          <View style={styles.spunkyFooter}>
            <TextReg style={styles.emojiText}>🎉</TextReg>
            <TextReg style={styles.spunkyText}>
              Phew! You made it. See you at the event!
            </TextReg>
            {ticketsButton}
          </View>
        )}

      <SessionDetailsModal />
      <SessionFilter
        filterCategoryIds={filterCategoryIds}
        setFilterCategoryIds={setFilterCategoryIds}
        filterTagIds={filterTagIds}
        setFilterTagIds={setFilterTagIds}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        selectedSpeakerId={selectedSpeakerId}
        setSelectedSpeakerId={setSelectedSpeakerId}
        showViewedOnly={showViewedOnly}
        setShowViewedOnly={setShowViewedOnly}
      />
    </PageContainer>
  );
}

export default withAuthGate(SessionVoting, { requireAuth: isNative });

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    eventNameContainer: {
      paddingTop: 12,
      alignItems: 'center',
      backgroundColor: Colors.purple0,
    },
    eventNameText: {
      fontSize: isMobile ? 24 : 28,
      textAlign: 'center',
      letterSpacing: 0.5,
    },
    eventDescriptionContainer: {
      marginTop: 8,
      paddingHorizontal: 8,
    },
    eventDescriptionText: {
      fontSize: isMobile ? 14 : 16,
      textAlign: 'center',
      lineHeight: isMobile ? 20 : 22,
    },
    learnMoreLinkContainer: {
      textDecorationLine: 'none',
    },
    learnMoreLink: {
      color: Colors.purple1,
    },
    filterButtonContainer: {
      position: 'relative',
    },
    filterCountBadge: {
      position: 'absolute',
      top: -4,
      right: -4,
      backgroundColor: Colors.pink0,
      width: 18,
      height: 18,
      borderRadius: 9,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1,
    },
    filterCountText: {
      color: '#FFFFFF',
      fontSize: 12,
      lineHeight: 14,
      fontWeight: '500',
    },
    sessionCard: {
      marginVertical: isMobile ? 12 : 16,
    },
    loginLink: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 8,
    },
    loginText: {
      color: Colors.text,
      fontSize: 14,
    },
    headerLeftContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    headerRightContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    cardsList: {
      flexGrow: 1,
      paddingBottom: 24,
      paddingHorizontal: 8, // Added horizontal padding to prevent box shadow cutoff
    },
    spunkyFooter: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      paddingBottom: 24,
    },
    emojiText: {
      fontSize: 32,
      textAlign: 'center',
    },
    spunkyText: {
      fontSize: isMobile ? 18 : 20,
      textAlign: 'center',
      lineHeight: isMobile ? 24 : 26,
      marginBottom: 8,
    },
    ticketsButton: {
      marginHorizontal: isMobile ? 0 : 24, // Reduced padding on mobile
    },
  });
